// About.jsx - Página Nosotros para FeelLab con audio integrado
import React, { useEffect, useRef, useState } from 'react';
import { 
  FaHeart, 
  FaPaw, 
  FaGraduationCap, 
  FaBrush,
  FaUsers,
  FaBookOpen,
  FaAward,
  FaLightbulb,
  FaQuoteLeft,
  FaUniversity,
  FaEnvelope,
  FaLinkedin,
  FaPlay,
  FaPause,
  FaStop,
  FaVolumeUp,
  FaVolumeDown,
  FaVolumeMute,
  FaMusic
} from 'react-icons/fa';

const About = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);

  // Inicializar audio al cargar la página
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
      
      // Reproducir automáticamente cuando se carga la página
      const playAudio = async () => {
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (error) {
          console.log('Autoplay bloqueado por el navegador:', error);
          // El autoplay fue bloqueado, pero los controles estarán disponibles
        }
      };

      // Event listeners
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);

      // Intentar reproducir después de que se carguen los metadatos
      if (audio.readyState >= 1) {
        playAudio();
      } else {
        audio.addEventListener('loadedmetadata', playAudio, { once: true });
      }

      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, []);

  // Controles de audio
  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play();
        setIsPlaying(true);
      }
    }
  };

  const stopAudio = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      if (isMuted) {
        audio.volume = volume;
        setIsMuted(false);
      } else {
        audio.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (audio && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return FaVolumeMute;
    if (volume < 0.5) return FaVolumeDown;
    return FaVolumeUp;
  };

  const teamMembers = [
    {
      id: 1,
      name: "Leydi Diana Saltarín Álvarez",
      role: "",
      description: ".",
      expertise: ["expertise", "", ""],
      icon: FaGraduationCap
    },
    {
      id: 2,
      name: "Leidylizeth Hernández Chajin",
      role: "",
      description: ".",
      expertise: ["expertise", "", ""],
      icon: FaBrush
    },
    {
      id: 3,
      name: "Dr. Freddy Marín González",
      role: "",
      description: ".",
      expertise: ["expertise", "", ""],
      icon: FaBookOpen
    }
  ];

  const achievements = [
    {
      title: "Validación Intersubjetiva",
      description: "Propuesta validada por expertos con calificaciones superiores a 4.5/5.0",
      icon: FaAward,
      value: "4.7/5.0"
    },
    {
      title: "Innovación Metodológica",
      description: "Integración única de arte, emociones y empatía animal",
      icon: FaLightbulb,
      value: "100% Original"
    },
    {
      title: "Impacto Educativo",
      description: "Modelo replicable en instituciones de educación básica",
      icon: FaUsers,
      value: "Escalable"
    }
  ];

  const zones = [
    { name: "Creación Artística", icon: "🖌️", description: "Espacio para expresión creativa" },
    { name: "Sensorial-Emocional", icon: "🌈", description: "Zona de exploración sensorial" },
    { name: "Vínculo Animal", icon: "🐢", description: "Conexión con seres vivos" },
    { name: "Escucha Activa", icon: "💬", description: "Círculos del sentir" },
    { name: "Introspección", icon: "🌿", description: "Rincón de calma y reflexión" }
  ];

  const VolumeIcon = getVolumeIcon();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 py-16 relative overflow-hidden">
      {/* Audio element */}
      <audio 
        ref={audioRef} 
        preload="auto"
        className="hidden"
      >
        <source src="/Audio/audio1.mp4" type="audio/mp4" />
        <source src="/Audio/audio1.mp3" type="audio/mpeg" />
        Tu navegador no soporta el elemento de audio.
      </audio>

      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 text-6xl text-emerald-500 animate-bounce">🎨</div>
        <div className="absolute top-40 right-20 text-4xl text-teal-500 animate-pulse">🐢</div>
        <div className="absolute bottom-32 left-20 text-5xl text-sky-500 animate-bounce delay-300">💚</div>
        <div className="absolute bottom-20 right-32 text-3xl text-emerald-600 animate-pulse delay-700">✨</div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 bg-white/80 rounded-full px-8 py-4 backdrop-blur-sm mb-6 shadow-lg border border-emerald-100">
            <FaPaw className="text-emerald-600 text-xl" />
            <span className="font-medium text-emerald-800">Nuestro Equipo</span>
            <FaPaw className="text-emerald-600 text-xl" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-emerald-900 mb-6 leading-tight">
            Sobre
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {' '}FeelLab
            </span>
          </h1>
          
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
              <FaQuoteLeft className="text-emerald-500 text-3xl mb-4 mx-auto" />
              <p className="text-lg text-emerald-800 leading-relaxed italic">
                "FeelLab no es simplemente un aula decorada, sino un ecosistema emocional sensible y transformador. 
                "
              </p>
            </div>
          </div>

          <p className="text-lg text-emerald-700 max-w-3xl mx-auto leading-relaxed">
            Un proyecto de investigación pedagógica de la Universidad de la Costa CUC que integra 
            creatividad, emociones y empatía animal para transformar la educación infantil.
          </p>
        </div>

        {/* Control de Audio - Ubicado después del header */}
        <div className="mb-16 flex justify-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-emerald-100 max-w-2xl w-full">
            <div className="flex items-center space-x-3 mb-4">
              <FaMusic className="text-emerald-600 text-xl" />
              <h3 className="text-lg font-bold text-emerald-800">Audio Presentación FeelLab</h3>
            </div>
            
            {/* Controles principales */}
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={togglePlayPause}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                {isPlaying ? <FaPause className="text-lg" /> : <FaPlay className="text-lg ml-0.5" />}
              </button>
              
              <button
                onClick={stopAudio}
                className="bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <FaStop className="text-lg" />
              </button>

              {/* Barra de progreso */}
              <div className="flex-1 mx-4">
                <div 
                  className="bg-gray-200 rounded-full h-2 cursor-pointer relative overflow-hidden"
                  onClick={handleSeek}
                >
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-300"
                    style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-emerald-600 mt-1">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Control de volumen */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleMute}
                  className="text-emerald-600 hover:text-emerald-800 p-1 transition-colors"
                >
                  <VolumeIcon className="text-lg" />
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${(isMuted ? 0 : volume) * 100}%, #e5e7eb ${(isMuted ? 0 : volume) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>
            </div>

            {/* Estado del audio */}
            <div className="text-center">
              <p className="text-sm text-emerald-600">
                {isPlaying ? (
                  <span className="flex items-center justify-center space-x-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                    <span>Reproduciendo...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center space-x-2">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    <span>Audio pausado</span>
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Información institucional */}
        <div className="mb-16 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <FaUniversity className="text-emerald-600 text-2xl" />
                <h2 className="text-2xl font-bold text-emerald-800">Universidad de la Costa CUC</h2>
              </div>
              <p className="text-emerald-700 mb-4">
                <strong>Departamento de Posgrados</strong><br />
                Programa Maestría en Educación<br />
                Modalidad Presencial<br />
                Barranquilla, Colombia
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-emerald-600">
                  <FaHeart className="text-sm" />
                  <span className="text-sm">Enfoque en educación emocional</span>
                </div>
                <div className="flex items-center space-x-2 text-emerald-600">
                  <FaBrush className="text-sm" />
                  <span className="text-sm">Mediación didáctica de las artes</span>
                </div>
                <div className="flex items-center space-x-2 text-emerald-600">
                  <FaPaw className="text-sm" />
                  <span className="text-sm">Desarrollo de empatía animal</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-4">🏛️</div>
              <h3 className="font-bold text-emerald-800 mb-2">Proyecto de Investigación</h3>
              <p className="text-emerald-700 text-sm">
                Propuesta pedagógica validada académicamente para la transformación 
                de espacios educativos en primera infancia.
              </p>
            </div>
          </div>
        </div>

        {/* Equipo de trabajo */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-emerald-800 mb-8 text-center">
            Nuestro Equipo de Investigación
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member) => {
              const IconComponent = member.icon;
              return (
                <div
                  key={member.id}
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-500 border border-emerald-100"
                >
                  {/* Avatar placeholder */}
                  <div className="bg-gradient-to-br from-emerald-100 to-teal-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="text-emerald-600 text-3xl" />
                  </div>
                  
                  {/* Información */}
                  <div className="text-center">
                    <h3 className="font-bold text-xl text-emerald-900 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-emerald-600 font-medium mb-3">
                      {member.role}
                    </p>
                    <p className="text-emerald-700 text-sm mb-4 leading-relaxed">
                      {member.description}
                    </p>
                    
                    {/* Áreas de experticia */}
                    <div className="space-y-2">
                      {member.expertise.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-block text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium border border-emerald-200 mr-2 mb-1"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Metodología - Zonas del laboratorio */}
        <div className="mb-16 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
          <h2 className="text-3xl font-bold text-emerald-800 mb-6 text-center">
            Metodología FeelLab
          </h2>
          <p className="text-emerald-700 text-center mb-8 max-w-3xl mx-auto">
            Nuestro laboratorio se estructura en cinco zonas especializadas que integran 
            arte, emociones y empatía animal para crear experiencias de aprendizaje transformadoras.
          </p>
          
          <div className="grid md:grid-cols-5 gap-4">
            {zones.map((zone, index) => (
              <div
                key={index}
                className="text-center group hover:scale-105 transition-transform duration-300"
              >
                <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl p-4 mb-3 group-hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{zone.icon}</div>
                  <h3 className="font-bold text-emerald-800 text-sm mb-2">
                    {zone.name}
                  </h3>
                  <p className="text-emerald-600 text-xs">
                    {zone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logros y validación */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-emerald-800 mb-8 text-center">
            Validación y Logros
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-emerald-100 text-center"
                >
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="text-white text-2xl" />
                  </div>
                  <h3 className="font-bold text-emerald-900 mb-2">{achievement.title}</h3>
                  <p className="text-emerald-700 text-sm mb-3">{achievement.description}</p>
                  <div className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-bold">
                    {achievement.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Misión y Visión */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <FaLightbulb className="text-emerald-600 text-2xl" />
              <h3 className="text-2xl font-bold text-emerald-800">Nuestra Misión</h3>
            </div>
            <p className="text-emerald-700 leading-relaxed">
              Transformar los espacios educativos tradicionales en ecosistemas emocionales 
              que promuevan la empatía, la creatividad y el bienestar integral de los niños 
              a través de la integración innovadora del arte y la conexión con los animales.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-teal-100 to-sky-100 rounded-3xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <FaHeart className="text-teal-600 text-2xl" />
              <h3 className="text-2xl font-bold text-teal-800">Nuestra Visión</h3>
            </div>
            <p className="text-teal-700 leading-relaxed">
              Ser un modelo pedagógico de referencia nacional e internacional que inspire 
              la creación de entornos educativos empáticos, donde cada niño pueda desarrollar 
              plenamente su potencial emocional, creativo y social.
            </p>
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">¿Quieres saber más sobre FeelLab?</h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Únete a nosotros en esta misión de transformar la educación a través 
              de la empatía, la creatividad y el amor por los animales.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-emerald-600 px-8 py-3 rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2">
                <FaEnvelope />
                <span>Contáctanos</span>
              </button>
              <button className="border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white hover:text-emerald-600 transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2">
                <FaBookOpen />
                <span>Conoce más</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos CSS personalizados para el slider de volumen */}
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default About;